// Composable anchored dropdown — the canonical generic menu (DESIGN-SYSTEM.md §5).
// Built on @floating-ui/react: click to open, portal to <body>, Esc + click-outside
// to close, arrow-key navigation (kicks in for lists), and danger-red items.
//
// Compose it:
//   <Menu>
//     <MenuButton className="…">Open</MenuButton>
//     <MenuItems>
//       <MenuItem onClick={rename}>Rename</MenuItem>
//       <MenuItem danger onClick={remove}>Delete</MenuItem>
//     </MenuItems>
//   </Menu>
//
// The menu context + the arrow-nav hook live in this module (not exported), so the
// file exports only components — Fast-Refresh safe.

import {
  createContext,
  useContext,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type ReactNode,
} from "react";
import {
  FloatingFocusManager,
  FloatingList,
  FloatingPortal,
  autoUpdate,
  flip,
  offset,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useListItem,
  useListNavigation,
  useRole,
  type Placement,
} from "@floating-ui/react";
import { cx } from "./classNames.js";

// ---- context ----------------------------------------------------------------

interface MenuContextValue {
  readonly open: boolean;
  readonly refs: ReturnType<typeof useFloating>["refs"];
  readonly floatingStyles: ReturnType<typeof useFloating>["floatingStyles"];
  readonly context: ReturnType<typeof useFloating>["context"];
  readonly isPositioned: boolean;
  readonly activeIndex: number | null;
  readonly getReferenceProps: ReturnType<typeof useInteractions>["getReferenceProps"];
  readonly getFloatingProps: ReturnType<typeof useInteractions>["getFloatingProps"];
  readonly getItemProps: ReturnType<typeof useInteractions>["getItemProps"];
  readonly setOpen: (v: boolean) => void;
}

const MenuCtx = createContext<MenuContextValue | null>(null);

function useMenuCtx(name: string): MenuContextValue {
  const ctx = useContext(MenuCtx);
  if (!ctx) throw new Error(`<${name}> must be used inside <Menu>`);
  return ctx;
}

// ---- Menu (root) ------------------------------------------------------------

export function Menu({
  children,
  placement = "bottom-end",
}: {
  readonly children: ReactNode;
  readonly placement?: Placement;
}) {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const listRef = useRef<Array<HTMLElement | null>>([]);

  const { refs, floatingStyles, context, isPositioned } = useFloating({
    open,
    onOpenChange: (v) => {
      setOpen(v);
      if (!v) setActiveIndex(null);
    },
    placement,
    whileElementsMounted: autoUpdate,
    middleware: [offset(6), flip({ padding: 8 }), shift({ padding: 8 })],
  });

  const click = useClick(context, { event: "click" });
  const dismiss = useDismiss(context, { escapeKey: true, outsidePress: true });
  const role = useRole(context, { role: "menu" });
  // Arrow-key navigation over the registered items — the canon behaviour for
  // lists (>4 items) but harmless on short menus.
  const listNav = useListNavigation(context, {
    listRef,
    activeIndex,
    onNavigate: setActiveIndex,
    loop: true,
  });
  const { getReferenceProps, getFloatingProps, getItemProps } = useInteractions([
    click,
    dismiss,
    role,
    listNav,
  ]);

  return (
    <MenuCtx.Provider
      value={{
        open,
        refs,
        floatingStyles,
        context,
        isPositioned,
        activeIndex,
        getReferenceProps,
        getFloatingProps,
        getItemProps,
        setOpen,
      }}
    >
      <FloatingList elementsRef={listRef}>{children}</FloatingList>
    </MenuCtx.Provider>
  );
}

// ---- MenuButton -------------------------------------------------------------

export function MenuButton({
  children,
  className,
  ...rest
}: {
  readonly children: ReactNode;
  readonly className?: string;
} & Omit<ButtonHTMLAttributes<HTMLButtonElement>, "children" | "className">) {
  const { refs, getReferenceProps, open } = useMenuCtx("MenuButton");
  return (
    <button
      ref={refs.setReference}
      type="button"
      aria-expanded={open}
      className={className}
      {...getReferenceProps(rest)}
    >
      {children}
    </button>
  );
}

// ---- MenuItems --------------------------------------------------------------

export function MenuItems({
  children,
  className,
}: {
  readonly children: ReactNode;
  readonly className?: string;
}) {
  const { open, refs, floatingStyles, context, isPositioned, getFloatingProps } =
    useMenuCtx("MenuItems");

  if (!open) return null;

  return (
    <FloatingPortal>
      <FloatingFocusManager context={context} modal={false} initialFocus={-1}>
        <div
          ref={refs.setFloating}
          style={{
            ...floatingStyles,
            opacity: isPositioned ? 1 : 0,
            pointerEvents: isPositioned ? undefined : "none",
          }}
          className={
            className ??
            "au-panel-in z-overlay min-w-44 overflow-hidden rounded-md border border-slate-700 bg-slate-900/95 py-1 shadow-xl shadow-black/40 backdrop-blur outline-none"
          }
          {...getFloatingProps()}
        >
          {children}
        </div>
      </FloatingFocusManager>
    </FloatingPortal>
  );
}

// ---- MenuItem ---------------------------------------------------------------

export function MenuItem({
  children,
  onClick,
  danger = false,
  disabled = false,
  keepOpen = false,
  className,
}: {
  readonly children: ReactNode;
  readonly onClick?: () => void;
  /** Destructive action — renders red (DESIGN-SYSTEM.md §5, law 2). */
  readonly danger?: boolean;
  readonly disabled?: boolean;
  /** Keep the menu open after click (for async items that show inline status). */
  readonly keepOpen?: boolean;
  readonly className?: string;
}) {
  const { setOpen, activeIndex, getItemProps } = useMenuCtx("MenuItem");
  // Register with the FloatingList so arrow-key navigation can reach this row.
  const item = useListItem();
  const isActive = item.index === activeIndex;

  const handleClick = () => {
    if (disabled) return;
    onClick?.();
    if (!keepOpen) setOpen(false);
  };

  // When a custom className is provided it fully replaces `base`, but we still
  // merge in the active-state highlight so arrow-key navigation remains visible.
  const activeClass = isActive
    ? danger ? "bg-red-500/10 text-red-300" : "bg-slate-800 text-slate-100"
    : undefined;

  // Danger renders red at rest (DESIGN-SYSTEM §5 law 2), intensifying on hover —
  // the text color lives in the branch (not the base) so there's no
  // slate-vs-red class conflict, mirroring ContextMenu's danger styling.
  const base = cx(
    "flex w-full items-center gap-2 px-3 py-1.5 text-[13px] focus-visible:outline-none",
    danger
      ? "text-red-400 hover:bg-red-500/10 hover:text-red-300 focus-visible:bg-red-500/10"
      : "text-slate-300 hover:bg-slate-800 hover:text-slate-100 focus-visible:bg-slate-800",
    activeClass,
    disabled && "cursor-not-allowed opacity-50",
  );

  return (
    <button
      ref={item.ref}
      type="button"
      role="menuitem"
      tabIndex={isActive ? 0 : -1}
      disabled={disabled}
      className={cx(className ?? base, className && activeClass)}
      {...getItemProps({ onClick: handleClick })}
    >
      {children}
    </button>
  );
}
